import { useTranslation } from "react-i18next";

interface TimeSlotPickerProps {
  unavailableSlots: Set<string>;
  selectedTime: string | null;
  onSelectTime: (time: string) => void;
}

interface RawSlotGroup {
  labelAr: string;
  labelEn: string;
  icon: string;
  color: string;
  slots: { time: string; displayAr: string; displayEn: string }[];
}

const RAW_SLOT_GROUPS: RawSlotGroup[] = [
  {
    labelAr: "الصباح",
    labelEn: "Morning",
    icon: "ri-sun-line",
    color: "text-amber-500",
    slots: [
      { time: "08:00", displayAr: "8:00 ص", displayEn: "8:00 AM" },
      { time: "08:30", displayAr: "8:30 ص", displayEn: "8:30 AM" },
      { time: "09:00", displayAr: "9:00 ص", displayEn: "9:00 AM" },
      { time: "09:30", displayAr: "9:30 ص", displayEn: "9:30 AM" },
      { time: "10:00", displayAr: "10:00 ص", displayEn: "10:00 AM" },
      { time: "10:30", displayAr: "10:30 ص", displayEn: "10:30 AM" },
      { time: "11:00", displayAr: "11:00 ص", displayEn: "11:00 AM" },
      { time: "11:30", displayAr: "11:30 ص", displayEn: "11:30 AM" },
    ],
  },
  {
    labelAr: "الظهيرة",
    labelEn: "Afternoon",
    icon: "ri-sun-foggy-line",
    color: "text-orange-500",
    slots: [
      { time: "12:00", displayAr: "12:00 م", displayEn: "12:00 PM" },
      { time: "12:30", displayAr: "12:30 م", displayEn: "12:30 PM" },
      { time: "13:00", displayAr: "1:00 م", displayEn: "1:00 PM" },
      { time: "13:30", displayAr: "1:30 م", displayEn: "1:30 PM" },
      { time: "14:00", displayAr: "2:00 م", displayEn: "2:00 PM" },
      { time: "14:30", displayAr: "2:30 م", displayEn: "2:30 PM" },
    ],
  },
  {
    labelAr: "المساء",
    labelEn: "Evening",
    icon: "ri-moon-line",
    color: "text-indigo-400",
    slots: [
      { time: "16:00", displayAr: "4:00 م", displayEn: "4:00 PM" },
      { time: "16:30", displayAr: "4:30 م", displayEn: "4:30 PM" },
      { time: "17:00", displayAr: "5:00 م", displayEn: "5:00 PM" },
      { time: "17:30", displayAr: "5:30 م", displayEn: "5:30 PM" },
      { time: "18:00", displayAr: "6:00 م", displayEn: "6:00 PM" },
      { time: "18:30", displayAr: "6:30 م", displayEn: "6:30 PM" },
      { time: "19:00", displayAr: "7:00 م", displayEn: "7:00 PM" },
      { time: "19:30", displayAr: "7:30 م", displayEn: "7:30 PM" },
    ],
  },
];

export default function TimeSlotPicker({ unavailableSlots, selectedTime, onSelectTime }: TimeSlotPickerProps) {
  const { i18n } = useTranslation();
  const isAr = !i18n.language?.startsWith("en");

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex-1">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-bold text-gray-800 text-sm">
          {isAr ? "المواعيد المتاحة" : "Available Times"}
        </h4>
        <div className="flex items-center gap-3 text-[10px] text-gray-400">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 bg-brand-forest-600 rounded-sm"></span>
            {isAr ? "محدد" : "Selected"}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 bg-gray-100 rounded-sm border border-gray-200"></span>
            {isAr ? "محجوز" : "Booked"}
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2.5 h-2.5 bg-brand-cream-100 rounded-sm border border-brand-cream-200"></span>
            {isAr ? "متاح" : "Available"}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {RAW_SLOT_GROUPS.map((group) => {
          const availableCount = group.slots.filter((s) => !unavailableSlots.has(s.time)).length;
          const groupLabel = isAr ? group.labelAr : group.labelEn;
          return (
            <div key={group.labelAr}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${group.icon} ${group.color} text-sm`}></i>
                </div>
                <span className="text-xs font-semibold text-gray-600">{groupLabel}</span>
                <span className="text-[10px] text-gray-400 ms-auto">
                  {isAr ? `${availableCount} موعد متاح` : `${availableCount} available`}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {group.slots.map((slot) => {
                  const isUnavailable = unavailableSlots.has(slot.time);
                  const isSelected = selectedTime === slot.time;
                  const displayTime = isAr ? slot.displayAr : slot.displayEn;
                  return (
                    <button
                      key={slot.time}
                      onClick={() => !isUnavailable && onSelectTime(slot.time)}
                      disabled={isUnavailable}
                      className={`
                        py-2 px-1 rounded-xl text-xs font-semibold transition-all duration-200 whitespace-nowrap
                        ${isUnavailable
                          ? "bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                          : isSelected
                          ? "bg-brand-forest-600 text-white cursor-pointer scale-105 shadow-sm"
                          : "bg-brand-cream-50 text-gray-700 hover:bg-brand-cream-100 hover:text-brand-forest-700 cursor-pointer border border-brand-cream-200"
                        }
                      `}
                    >
                      {displayTime}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
